








import dateFormat from 'date-fns/format'
import { Either, left, right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component } from 'vue-property-decorator'

import SpaErrorDialog from '@/components/organisms/dialog/ErrorDialog.vue'
import { log } from '@/log'
import { CommerceSalesOrder } from '@/models/api/commerce-sales-order'
import { isReorderByUser } from '@/models/order-history/supersede-status'
import LoadingMixin from '@spa/mixins/loading'
import { PluginMixin } from '@spa/mixins/plugin'
import { CartService } from '@spa/services/api/cart'
import { OrderHistoryApiService } from '@spa/services/api/orderHistory'
import { OrderService, OrderSource } from '@spa/services/order'
import CompleteDataBuilder from '@spa/services/order/complete-data-builder'
import { CartItem } from '@spa/store/modules/cart/types'
import { CreateCartItemsThroughInjectProductclass } from '@spa/store/modules/orderConfirm/converter/create-cart-items-through-inject-productclass'
import { OrderResult } from '@spa/store/modules/orderConfirm/types'
import { User } from '@spa/store/modules/user/types'

@Component({
  components: {
    SpaErrorDialog,
  },
  mixins: [
    LoadingMixin,
  ],
})
export default class OrderCompleteById extends Mixins<PluginMixin>(PluginMixin) {

  status: Status = Status.Pending
  errorMessages: string[] = []

  readonly defaultErrorMessages: string[] = [
    '注文は完了しましたが、ご注文内容をご確認いただくページの表示に失敗しました。',
    '',
    'お手数ですが、マイページ＞購入履歴よりご注文内容をご確認ください。',
    '※マイページへ移動します',
  ]

  cartService: CartService = new CartService()
  orderService: OrderService = OrderService.create({
    orderSource: OrderSource.asUndefined(),
  })
  orderHistoryApiService: OrderHistoryApiService = new OrderHistoryApiService()

  commerceSalesOrder: CommerceSalesOrder = new CommerceSalesOrder({})
  cartItems: CartItem[] = []

  get modalMessage(): string {
    return this.errorMessages.join('<br>')
  }

  get showModal(): boolean {
    return this.status === Status.Failed
  }

  get user(): User {
    return this.$store.state.user.user
  }

  closeModal() {
    this.status = Status.Completed
    this.errorMessages = []

    this.$router.push('/mypage')
  }

  async created() {
    await this.$store.dispatch('view/startLoading')

    this.status = Status.Loading

    const { id } = this.$route.params
    const [
      commerceSalesOrder,
    ] = await Promise.all([
      this.fetchOrderDetail(id),
      this.$store.dispatch('appSetting/load', { brand: null }),
      this.$store.dispatch('contact/load'),
      this.$store.dispatch('user/get'),
      // FIXME:
      // 下記は部分的に重複しているデータ処理を含んでいる
      // storeが肥大化しているため利用を避けたかったが、カタログのみであるかどうかの判定は
      // 既存のstoreのロジックを利用する。
      // それ以外はcommerce_sales_orderにのみ依存して取得できるので、
      // commerce_sales_orderのオブジェクトを利用する。
      this.$store.dispatch('orderHistory/get', id),
    ])
    if (commerceSalesOrder.isLeft()) {
      log.error(commerceSalesOrder.value)
      this.errorMessages = this.defaultErrorMessages
      this.status = Status.Failed
      await this.$store.dispatch('view/stopLoading')

      return
    }
    this.commerceSalesOrder = commerceSalesOrder.value

    const cartItems = await this.fetchCartItems(this.commerceSalesOrder.cartItemIds)
    if (cartItems.isLeft()) {
      log.error(cartItems.value)
      this.errorMessages = this.defaultErrorMessages
      this.status = Status.Failed
      await this.$store.dispatch('view/stopLoading')

      return
    }
    this.cartItems = cartItems.value

    this.setOrderComplete()

    await this.$store.dispatch('view/stopLoading')

    const nextPath = this.isReorder ?
      '/order/complete?reorder=true' :
      '/order/complete'

    this.$router.push(nextPath)
  }

  async fetchOrderDetail(id: string): Promise<Either<Error, CommerceSalesOrder>> {
    const result = await this.orderHistoryApiService.get(id)

    if (result.isLeft()) {
      return left(result.value)
    }

    return right(new CommerceSalesOrder(_.get(result.value, 'body.data')))
  }

  async fetchCartItems(cartIds: string[]): Promise<Either<Error, CartItem[]>> {
    /**
     * 以前まで再注文によって作成された注文にはcart_idが含まれていなかった
     * Engineとして必須のフィールドではないので、検証して空であればエラーとする
     * @see https://github.com/Everforth/growthpalette-front/issues/7843#issuecomment-1943415180
     */
    if (cartIds.length === 0) {
      return left(new Error(`cardIds must not be empty`))
    }
    const result = await this.cartService.get('', {
      cart_status: undefined,
      ids: cartIds.join(','),
    })

    if (result.isLeft()) {
      return left(result.value)
    }

    const data: any[] = _.get(result.value.body, 'data', [])
    /**
     * 別のアカウントでログインしていてもcommerce_sales_orderはIDから取得できるが、commerce_user_cartは取得できない。
     * したがって、空で返ってくる可能性があるため検証する。
     * @see https://github.com/Everforth/growthpalette-front/issues/7802#issuecomment-1911999645
     */
    if (data.length === 0) {
      return left(new Error(`could not obtain any cart resources`))
    }

    const converter = new CreateCartItemsThroughInjectProductclass()

    return right(await converter.convert(data))
  }

  setOrderComplete() {
    const sortOrderResults = (results: OrderResult[]) => results
    const createOrderFromCartItems = (cartItems: CartItem[]) => {
      return this.orderService.createOrderFromCartItems(cartItems, this.deliveryWhen)
    }
    const builder = new CompleteDataBuilder(
      [this.commerceSalesOrder],
      [],
      this.cartItems,
      sortOrderResults,
      createOrderFromCartItems,
      {
        isFirstShareItem: this.isFirstShareItem,
        isFreeShipping: this.isFreeShipping,
        canScheduleDelivery: this.canScheduleDelivery,
      }
    )
    this.$store.dispatch('orderConfirm/setComplete', builder.build())
  }

  private get deliveryWhen(): string {
    return this.commerceSalesOrder.delivery.date ?
      dateFormat(this.commerceSalesOrder.delivery.date, 'YYYY年MM月DD日') :
      '未指定'
  }

  private get canScheduleDelivery(): boolean {
    return this.$store.getters['appSetting/scheduledDelivery']
  }

  private get isFirstShareItem() {
    return this.$store.getters['user/getFirstShareItem']
  }

  private get isFreeShipping(): boolean {
    return this.isFreeShippingOnCatalogOnly
  }

  private get isFreeShippingOnCatalogOnly(): boolean {
    return _.get(this.$store.state.orderHistory, 'item.isFreeShippingOnCatalogOnly', false)
  }

  private get isReorder(): boolean {
    return isReorderByUser(this.commerceSalesOrder)
  }
}

enum Status {
  Pending,
  Loading,
  Failed,
  Completed,
}
